<template>
  <div class="prompt-test-layout">
    <div class="stars-bg">
      <div v-for="n in 40" :key="n" class="star" :style="{ left: Math.random()*100+'%', top: Math.random()*100+'%', width: (Math.random()*2+1)+'px', height: (Math.random()*2+1)+'px', animationDelay: (Math.random()*4)+'s', animationDuration: (Math.random()*3+2)+'s' }"></div>
    </div>
    <div class="top-bar" v-if="step < 15">
      <button class="back-btn" v-if="step > 1 && step <= 5" @click="goBack">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      </button>
      <span v-else class="back-spacer"></span>
      <span class="uid-text">{{ userId || 'Astro Chat Test 2' }}</span>
      <span class="step-text">{{ step >= 6 && step <= 13 ? (answeredCount + 6) : step }}/14</span>
    </div>

    <div class="step-container">
      <!-- Step 1: Intro -->
      <div v-if="step === 1" class="step-intro">
        <div class="intro-card">
          <h1>Astro Chat Quality Test 2</h1>
          <p>You will enter your birth details and review a set of astrology answers based on your chart.</p>
          <p>After each answer, you can ask additional questions if something feels missing or unclear.</p>
          <p>We want your <strong>brutally honest</strong> reaction to each answer.</p>
          <p>Do not try to be polite. Honest negative feedback is more valuable than kind feedback.</p>
          <p>If possible, please say your thoughts out loud as you go.</p>
          <button class="primary-btn" @click="step = 2">Start Test</button>
        </div>
      </div>

      <!-- Step 2: Birth Info -->
      <step-birth-info v-if="step === 2" @next="onBirthComplete" @summary-complete="onSummaryComplete" />

      <!-- Step 3: Interest -->
      <step-interest v-if="step === 3" @next="onInterestComplete" />

      <!-- Step 4: What They Want -->
      <step-want v-if="step === 4" @next="onWantComplete" />

      <!-- Step 5: Transition -->
      <div v-if="step === 5" class="step-transition">
        <div class="transition-card">
          <p>Next, you'll see 8 questions with answers based on your birth chart.</p>
          <p>After each answer, you can score it and optionally <strong>ask follow-up questions</strong>.</p>
          <button class="primary-btn" @click="startQuestions">Continue</button>
        </div>
      </div>

      <!-- Steps 6-13: Benchmark Questions with Follow-up -->
      <template v-if="step >= 6 && step <= 13 && currentQuestion">
        <step-benchmark-with-followup
          v-if="!currentQuestion.error"
          :key="currentQuestion.id"
          :question-text="currentQuestion.text"
          :question-index="answeredCount"
          :total-questions="questionQueue.length"
          :answer="currentQuestion.answer"
          @complete="onQuestionComplete"
        />
        <div v-else class="error-state">
          <p class="error-question">{{ currentQuestion.text }}</p>
          <p class="error-msg">Could not get an answer. <span v-if="currentQuestion.retries < 3">Please try again.</span></p>
          <p v-if="currentQuestion.retries >= 3" class="error-hint">There seems to be a problem with this question right now. You can skip it for now.</p>
          <div class="error-actions">
            <button class="primary-btn" @click="retryCurrentQuestion">Try Again</button>
            <button v-if="currentQuestion.retries >= 3" class="skip-btn" @click="skipCurrentQuestion">Skip This Question</button>
          </div>
        </div>
      </template>

      <!-- Step 14: Wrap Up -->
      <step-wrap-up v-if="step === 14" @submit="onWrapUp" />

      <!-- Step 15: Done -->
      <div v-if="step === 15" class="done-screen">
        <h1>Thank You!</h1>
        <p>Your feedback has been recorded. This will help us improve the quality of Astro Chat answers.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { initFirebase } from '../../firebase'
import { PromptTestService } from '../../services/promptTestService'

const { functions } = initFirebase()
import StepBirthInfo from '../promptTest/StepBirthInfo.vue'
import StepInterest from '../promptTest/StepInterest.vue'
import StepWant from '../promptTest/StepWant.vue'
import StepWrapUp from '../promptTest/StepWrapUp.vue'
import StepBenchmarkWithFollowup from './StepBenchmarkWithFollowup.vue'

const BENCHMARK_QUESTIONS = [
  'Who am I, really, according to my birth chart?',
  'Why have I been feeling off lately?',
  'Based on my birth chart, what kind of love and relationship dynamic do I actually need?',
  'Based on my birth chart alone, name 3 career paths that fit me best and explain each one briefly.',
  'Based on my current transits, what is the main energy influencing me right now, and how might it be affecting my life?',
  'Based on my birth chart and current transits, what do the stars suggest when I feel torn between staying where I am and making a big change?',
  'Based on my birth chart and current transits, what do I most need right now, and what is one practical step I can take?',
  'Based on my birth chart and current transits, what is the one most important thing I should focus on right now to move my life forward, and why?'
]

export default {
  name: 'PromptTest2Flow',
  components: { StepBirthInfo, StepInterest, StepWant, StepWrapUp, StepBenchmarkWithFollowup },
  setup() {
    const testService = new PromptTestService()
    const chatFn = httpsCallable(functions, 'chat')

    const step = ref(1)
    const testId = ref(null)
    const userId = ref(null)
    const userName = ref('')
    const contextData = ref({ interestLevel: null, whatTheyWant: '' })

    // Question queue - sequential, no prefetching (follow-ups alter chat context)
    const questionQueue = ref(BENCHMARK_QUESTIONS.map((text, i) => ({
      id: i,
      text,
      answer: null,
      fetching: false,
      error: false,
      retries: 0
    })))
    const currentQIdx = ref(0)
    const answeredQuestions = ref([])

    const currentQuestion = computed(() => questionQueue.value[currentQIdx.value] || null)
    const answeredCount = computed(() => answeredQuestions.value.length)

    const fetchAnswer = async (qIdx) => {
      const q = questionQueue.value[qIdx]
      if (!q || (q.answer && q.answer.trim()) || q.fetching) return
      q.fetching = true
      q.error = false
      q.answer = null
      try {
        console.log(`[Test2] Fetching q${q.id}: "${q.text.substring(0, 40)}..."`)
        const result = await Promise.race([
          chatFn({ message: q.text }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 60000))
        ])
        let content = result.data?.response
        if (Array.isArray(content)) content = content.join('')
        if (!content || !content.trim()) throw new Error('Empty response')
        q.answer = content
        q.error = false
        console.log(`[Test2] Answer q${q.id} ready, length: ${q.answer.length}`)
      } catch (e) {
        console.error(`[Test2] ERROR q${q.id}:`, e.message)
        q.retries = (q.retries || 0) + 1
        q.error = true
        q.answer = null
      }
      q.fetching = false
    }

    const retryCurrentQuestion = () => {
      const q = questionQueue.value[currentQIdx.value]
      if (q) {
        q.error = false
        q.answer = null
        q.fetching = false
        fetchAnswer(currentQIdx.value)
      }
    }

    const skipCurrentQuestion = () => {
      const q = questionQueue.value[currentQIdx.value]
      if (q) {
        const failed = questionQueue.value.splice(currentQIdx.value, 1)[0]
        failed.error = false
        failed.answer = null
        failed.retries = 0
        questionQueue.value.push(failed)
        // Fetch the new current question
        fetchAnswer(currentQIdx.value)
      }
    }

    const goBack = () => {
      if (step.value > 1 && step.value <= 5) {
        step.value--
      }
    }

    const scrollToTop = () => {
      const container = document.querySelector('.step-container')
      if (container) container.scrollTop = 0
    }

    const summaryReady = ref(false)

    const onSummaryComplete = () => {
      summaryReady.value = true
    }

    const startQuestions = () => {
      step.value = 6
      currentQIdx.value = 0
      fetchAnswer(0)
    }

    const onBirthComplete = async ({ uid, name }) => {
      userId.value = uid
      userName.value = name
      testId.value = await testService.createTestSession(uid, name, 2)
      step.value = 3
    }

    const onInterestComplete = (interest) => {
      contextData.value.interestLevel = interest
      step.value = 4
    }

    const onWantComplete = async (want) => {
      contextData.value.whatTheyWant = want
      if (testId.value) {
        await testService.saveContext(testId.value, contextData.value)
      }
      step.value = 5
      currentQIdx.value = 0
    }

    const onQuestionComplete = async (result) => {
      if (testId.value) {
        await testService.saveQuestionResult(testId.value, result)
      }
      answeredQuestions.value.push(result)
      scrollToTop()

      if (answeredQuestions.value.length >= BENCHMARK_QUESTIONS.length) {
        step.value = 14
      } else {
        currentQIdx.value++
        // Sequential: fetch only when needed, no prefetch
        fetchAnswer(currentQIdx.value)
      }
    }

    const onWrapUp = async (wrapUpData) => {
      if (testId.value) {
        await testService.saveWrapUp(testId.value, wrapUpData)
      }
      step.value = 15
    }

    return {
      step, userId, answeredCount, currentQuestion, questionQueue, currentQIdx,
      fetchAnswer, retryCurrentQuestion, skipCurrentQuestion, startQuestions, onSummaryComplete,
      onBirthComplete, onInterestComplete, onWantComplete, onQuestionComplete, onWrapUp, goBack
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.prompt-test-layout { position: fixed; inset: 0; color: #fff; display: flex; flex-direction: column; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; background: #0E0C1F; z-index: 50; overflow: hidden; }
.stars-bg { position: absolute; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.star { position: absolute; background: #fff; border-radius: 50%; opacity: 0.3; animation: twinkle 3s ease-in-out infinite; }
@keyframes twinkle { 0%, 100% { opacity: 0.2; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.3); } }
.top-bar { position: fixed; top: 0; left: 0; right: 0; height: 40px; display: flex; align-items: center; justify-content: space-between; padding: 0 16px; z-index: 100; background: rgba(14,12,31,0.8); }
.back-btn { width: 32px; height: 32px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; color: #a29bfe; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.back-btn:hover { background: rgba(108,99,255,0.12); border-color: #6c5ce7; }
.back-spacer { width: 32px; }
.uid-text { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px; font-weight: 700; color: #a29bfe; }
.step-text { font-size: 12px; font-weight: 700; color: #a29bfe; }
.step-container { flex: 1; overflow-y: auto; padding: 52px 20px 20px; position: relative; z-index: 1; }

/* Inline Intro */
.step-intro { display: flex; align-items: center; justify-content: center; min-height: 60vh; padding: 20px; }
.intro-card { max-width: 520px; width: 100%; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 40px 32px; text-align: center; display: flex; flex-direction: column; gap: 20px; }
.intro-card h1 { font-size: 28px; font-weight: 800; color: #fff; margin: 0; }
.intro-card p { font-size: 15px; line-height: 1.7; color: #9896a8; margin: 0; }
.intro-card strong { color: #a29bfe; }

/* Inline Transition */
.step-transition { display: flex; align-items: center; justify-content: center; min-height: 60vh; padding: 20px; }
.transition-card { max-width: 520px; width: 100%; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 40px 32px; text-align: center; display: flex; flex-direction: column; gap: 20px; }
.transition-card p { font-size: 16px; line-height: 1.7; color: #9896a8; margin: 0; }
.transition-card strong { color: #a29bfe; }

.primary-btn { padding: 16px 24px; background: #6c5ce7; border: none; border-radius: 12px; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; width: 100%; margin-top: 8px; font-family: inherit; }
.primary-btn:hover { background: #7c6ff0; box-shadow: 0 6px 20px rgba(108,99,255,0.35); }

.done-screen { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; min-height: 60vh; gap: 16px; }
.done-screen h1 { font-size: 28px; color: #a29bfe; }
.done-screen p { font-size: 15px; color: rgba(255,255,255,0.5); max-width: 400px; line-height: 1.6; }

.error-state { max-width: 680px; margin: 0 auto; padding: 40px 20px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 20px; }
.error-question { font-size: 18px; font-weight: 600; color: #fff; }
.error-msg { font-size: 14px; color: rgba(255,255,255,0.5); margin: 0; }
.error-hint { font-size: 13px; color: #fbbf24; margin: 0; }
.error-actions { display: flex; gap: 12px; width: 100%; max-width: 400px; }
.error-actions .primary-btn { flex: 1; }
.skip-btn { flex: 1; padding: 16px 24px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.skip-btn:hover { background: rgba(255,255,255,0.08); }
</style>
